import { LoadingButton } from '@mui/lab';
import { Box, CardContent } from '@mui/material';
import { useConfig } from '@revenuewell/logic-config';
import { useNotification } from '@revenuewell/logic-notification';
import { useOidc } from '@revenuewell/logic-oidc';
import { ImageLibraryService } from 'apps/wrapper/src/services/practice-portal/image-library/image-library-service';
import { ImageLibraryItem } from 'apps/wrapper/src/services/practice-portal/image-library/types/image-library-item';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { Card, CardTitle } from '../card/card.style';
import { CloseDialogButton, Dialog, DialogContent, DialogContentWrapper, DialogTitle } from '../dialog/dialog.styles';
import { NoDataComponent } from '../grid/no-data';
import { CropImageDialog } from './crop-image/crop-image';
import { ImageLibrarySkeleton } from './image-library.skeleton';
import { Hr, Image, ImageName, ImagesContainer, ImageWrapper, ToolbarSection } from './image-library.styles';

const MY_IMAGES_CATEGORY_ID = 6;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export interface ImageLibraryDialogProps {
  open: boolean;
  locationId: number;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
}

export const ImageLibraryDialog: FC<ImageLibraryDialogProps> = (props) => {
  const { open, locationId, onClose, onSelect } = props;
  const { config } = useConfig();
  const { oidcService } = useOidc();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState<ImageLibraryItem[]>([]);
  const [fullImagePath, setFullImagePath] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<ImageLibraryItem>();
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const imageLibraryService = useMemo(() => new ImageLibraryService(config, oidcService), [config, oidcService]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) loadImageList();
  }, [open]);

  const loadImageList = async () => {
    try {
      setIsLoading(true);

      const imageData = await imageLibraryService.getImageList(locationId, MY_IMAGES_CATEGORY_ID);

      if (imageData.ImagesList && imageData.ImagesList.length > 0) {
        setImages(imageData.ImagesList);
        setFullImagePath(imageData.OriginalImagePath);
        setSelectedImage(imageData.ImagesList[0]);
      }
    } catch (error) {
      showError('Unable to load images.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = async () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || !files[0]) return;

    const file = files[0];

    if (file.size > MAX_FILE_SIZE) {
      event.target.value = '';
      showError('File size exceeds 10 MB. Please select a smaller file.');
      return;
    }

    try {
      setIsUploading(true);
      const response = await imageLibraryService.uploadImage(locationId, MY_IMAGES_CATEGORY_ID, file);

      if (!response.Success) throw new Error('Unable to upload image.');

      showSuccess('Image uploaded successfully.');
      await loadImageList();
    } catch (error) {
      console.error('Error uploading files:', error);
      showError('Unable to upload image.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!selectedImage) return;

    try {
      setIsDeleting(true);

      const response = await imageLibraryService.deleteImage(locationId, selectedImage.FileName);

      if (!response.IsSuccess) throw new Error('Unable to delete image.');

      const updatedImages = images.filter((image) => image.FileName !== selectedImage.FileName);

      setImages(updatedImages);
      setSelectedImage(updatedImages[0]);
      showSuccess('Image deleted successfully.');
    } catch (error) {
      showError('Unable to delete image.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUseClick = () => {
    if (selectedImage) {
      onSelect(getFullImageUrl(selectedImage));
      onClose();
    }
  };

  const handleImageCrop = (imageUrl: string) => {
    onSelect(`${fullImagePath}${imageUrl}`);
    setCropDialogOpen(false);
    onClose();
  };

  const getFullImageUrl = (item: ImageLibraryItem) => {
    const segments = item.Url.split('/');
    const filenameWithExtension = segments[segments.length - 1];
    return `${fullImagePath}${filenameWithExtension}`;
  };

  return (
    <>
      <Dialog open={open} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            Image Library
            <CloseDialogButton onClick={onClose} />
          </DialogTitle>
          <DialogContent sx={{ width: '760px', padding: '40px 20px 0 20px' }}>
            <Card>
              <CardContent style={{ padding: 0 }}>
                <CardTitle sx={{ padding: '16px', margin: '0' }}>My Images</CardTitle>
                <Scrollbars autoHide style={{ height: '338px' }}>
                  <ImagesContainer>
                    {isLoading && <ImageLibrarySkeleton />}
                    {!isLoading && images.length === 0 && (
                      <Box sx={{ display: 'flex', margin: 'auto' }}>
                        <NoDataComponent />
                      </Box>
                    )}
                    {!isLoading &&
                      images.length > 0 &&
                      images.map((image: ImageLibraryItem) => (
                        <ImageWrapper
                          key={image.DisplayName}
                          onClick={() => setSelectedImage(image)}
                          sx={{ backgroundColor: selectedImage?.FileName === image.FileName ? '#EDEFF2' : 'white' }}
                        >
                          <Image src={image.Url} alt={image.Alt} />
                          <ImageName>{image.DisplayName}</ImageName>
                        </ImageWrapper>
                      ))}
                  </ImagesContainer>
                </Scrollbars>
                <Hr />
                <ToolbarSection>
                  <Box sx={{ display: 'flex', padding: '0 16px 0 0', width: '100%' }}>
                    <Box sx={{ marginRight: 'auto' }}>
                      <input
                        type="file"
                        accept="image/jpeg, image/png, image/gif, image/bmp, image/webp"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />
                      <LoadingButton
                        variant="contained"
                        loading={isUploading}
                        onClick={handleUploadClick}
                        sx={{ marginRight: 'auto', marginLeft: '0' }}
                        disabled={isDeleting}
                        disableElevation
                      >
                        Upload
                      </LoadingButton>
                      <LoadingButton
                        loading={isDeleting}
                        variant="outlined"
                        color="error"
                        onClick={handleDeleteClick}
                        disabled={!selectedImage || isUploading || selectedImage?.UsedByPractice}
                        disableElevation
                      >
                        Delete
                      </LoadingButton>
                    </Box>
                    <LoadingButton
                      loading={false}
                      variant="contained"
                      onClick={() => setCropDialogOpen(true)}
                      disabled={!selectedImage || isDeleting || isUploading}
                      disableElevation
                    >
                      Crop
                    </LoadingButton>
                    <LoadingButton
                      loading={false}
                      variant="contained"
                      disabled={!selectedImage || isDeleting || isUploading}
                      onClick={handleUseClick}
                      disableElevation
                    >
                      Use
                    </LoadingButton>
                  </Box>
                </ToolbarSection>
              </CardContent>
            </Card>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>

      {selectedImage && (
        <CropImageDialog
          open={cropDialogOpen}
          locationId={locationId}
          imageUrl={getFullImageUrl(selectedImage)}
          onCrop={handleImageCrop}
          onClose={() => setCropDialogOpen(false)}
        />
      )}
    </>
  );
};
